import React, { useCallback, useEffect, useRef } from 'react';

import * as S from './styles';
import { DsIcon, DsTypography } from '../';
import { IconIconAttribute } from '@wisereducacao-npm/design-system-core-web/dist/components/icon/types';
import { TypographyColorAttribute } from '@wisereducacao-npm/design-system-core-web/dist/components/typography/types';

import { useProductFlow } from 'hooks/useProductFlow';
import { useCart } from 'hooks/useCart';
import { useTranslations } from 'hooks/useTranslations';

interface FeedbackBoxProps {
  type: 'success' | 'error' | 'alert';
  message: string;
  title: string;
  scrollToTop: boolean;
  onClose?: VoidFunction;
  actionLinkHref?: string;
  actionLinkLabel?: string;
  showCallCenterLink?: boolean;
}

const icon: Record<string, IconIconAttribute> = {
  success: 'check_circle',
  alert: 'warning_circle',
  error: 'warning',
};

const color: Record<string, TypographyColorAttribute> = {
  success: 'feedback-success-pure',
  alert: 'feedback-alert-pure',
  error: 'feedback-error-pure',
};

export function FeedbackBox({
  message,
  title,
  type = 'error',
  scrollToTop,
  onClose,
  actionLinkHref,
  actionLinkLabel,
  showCallCenterLink,
}: FeedbackBoxProps) {
  const componentRef = useRef(null);
  const { legacyProductTheme } = useCart();
  const { tPages } = useTranslations();

  const { currentStep } = useProductFlow();

  const shouldShowCallCenterLink =
    showCallCenterLink ??
    (!!currentStep.props?.faqLink || legacyProductTheme?.data?.faqLink);

  const executeScroll = useCallback(
    () =>
      scrollToTop
        ? window?.scrollTo({
            behavior: 'smooth',
            top: 0,
            left: 0,
          })
        : componentRef.current?.scrollIntoView({
            behavior: 'smooth',
            block: 'center',
            inline: 'center',
          }),
    [scrollToTop]
  );

  useEffect(() => {
    const timeoutId = setTimeout(() => {
      !!componentRef && executeScroll();
    }, 200);

    return () => clearTimeout(timeoutId);
  }, [executeScroll]);

  return (
    <S.Wrapper type={type}>
      <DsIcon icon={icon[type]} color={color[type]} />

      <S.Content>
        <S.MessageGroup>
          <DsTypography
            component="paragraph"
            size="small-bold"
            color="neutral-pure"
          >
            {title}
          </DsTypography>
          <DsTypography
            component="paragraph"
            size="small"
            color="neutral-high"
            dangerouslySetInnerHTML={{ __html: message }}
          />
          {actionLinkHref && actionLinkLabel && (
            <DsTypography
              id="action-link-message"
              component="paragraph"
              size="small"
              color="neutral-high"
            >
              <S.FaqLink href={actionLinkHref}>{actionLinkLabel}</S.FaqLink>
            </DsTypography>
          )}
          {shouldShowCallCenterLink && (
            <DsTypography
              id="call-center-message"
              component="paragraph"
              size="small"
              color="neutral-high"
            >
              {tPages('faqIntro')}{' '}
              <S.FaqLink
                target="_blank"
                href={
                  currentStep.props?.faqLink ||
                  legacyProductTheme?.data?.faqLink
                }
                rel="noreferrer"
              >
                {tPages('faqLinkLabel')}
              </S.FaqLink>
              .
            </DsTypography>
          )}
        </S.MessageGroup>
      </S.Content>

      {onClose && (
        <S.IconContainer onClick={onClose}>
          <DsIcon icon="x" data-testid="close-icon" />
        </S.IconContainer>
      )}
    </S.Wrapper>
  );
}
